// ---------------------------------------------------------------------------
// Application shell
// ---------------------------------------------------------------------------
// Sidebar, top bar and content region.
//
// Navigation is built from the signed-in role's permissions, so a customer and
// an administrator see genuinely different products rather than the same menu
// with items greyed out. A greyed-out menu of things you cannot do is just a
// list of your own limitations.
//
// Responsive behaviour is a layout change, not a scale change:
//   desktop  full sidebar, three-column workspace
//   tablet   icon rail, context panel collapses into a drawer
//   mobile   sidebar becomes a sheet, workspace becomes tabs
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  BookOpen,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  Gauge,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Plus,
  Search as SearchIcon,
  ScrollText,
  Settings,
  Siren,
  SlidersHorizontal,
  Sun,
  Tags,
  Timer,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../ui/Primitives';
import api from '../api/client';
import './AppShell.css';

/** Persisted theme, applied to the document root. */
function useTheme() {
  const [theme, setTheme] = useState(
    () => document.documentElement.dataset.theme || 'light',
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('cvs-theme', theme);
    } catch {
      // Private browsing: the theme simply will not persist.
    }
  }, [theme]);

  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

export default function AppShell({ children }) {
  const { user, isInternal, can, signOut } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('cvs-nav-collapsed') === 'true',
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('cvs-nav-collapsed', String(collapsed));
  }, [collapsed]);

  // Unread badge. Polled, because a websocket for a number that changes every
  // few minutes is not worth the connection.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.notifications({ unread: true, limit: 1 });
        if (alive) setUnread(res.unread ?? 0);
      } catch {
        // A failing badge must not break the shell around it.
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [location.pathname]);

  const sections = useMemo(
    () => buildNavigation({ isInternal, can }),
    [isInternal, can],
  );

  return (
    <div className={`shell${collapsed ? ' shell-collapsed' : ''}`}>
      {mobileOpen && (
        <button
          className="shell-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`shell-nav${mobileOpen ? ' shell-nav-open' : ''}`}>
        
      <div className="shell-brand">
          
          <svg className="logo-icon" viewBox="0 0 44 28">
          <polyline points="2,14 9,14 13,4 17,24 22,7 26,21 29,14 39,14" stroke="#a78bfa" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="39" cy="14" r="3" fill="#a78bfa"/>
        </svg>
       
        

          {!collapsed && (
            <div className="col" style={{ minWidth: 0 }}>
              <span className="shell-brand-name">CliniqVoice</span>
              <span className="shell-brand-sub">
                {isInternal ? 'Support Operations' : 'Support'}
              </span>
            </div>
          )}
          <button
            className="shell-nav-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X size={16} />
          </button>
        </div>

        {!isInternal && (
          <div className="shell-nav-cta">
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => navigate('/portal/requests/new')}
              style={{ width: '100%' }}
            >
              {!collapsed && 'New request'}
            </Button>
          </div>
        )}

        <nav className="shell-nav-scroll">
          {sections.map((section) => (
            <div key={section.title} className="shell-nav-section">
              {!collapsed && <div className="shell-nav-heading">{section.title}</div>}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `shell-nav-item${isActive ? ' active' : ''}`}
                  title={collapsed ? item.label : undefined}
                  // Dismiss the mobile sheet here rather than in an effect on
                  // the route — leaving it open over the page the user just
                  // chose is a classic annoyance, and an effect that closes it
                  // is a synchronous setState the moment the route changes.
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon size={15} strokeWidth={2} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {!collapsed && item.badge ? <span className="shell-nav-badge">{item.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="shell-nav-foot">
          <button
            className="shell-nav-item"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div className="shell-main">
        <header className="shell-top">
          <button
            className="shell-menu-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={17} />
          </button>

          <GlobalSearch />

          <div className="shell-actions">
            <Button
              variant="ghost"
              size="sm"
              icon={theme === 'dark' ? Sun : Moon}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            />
            <NavLink to="/notifications" className="shell-bell" aria-label="Notifications">
              <Bell size={16} />
              {unread > 0 && <span className="shell-bell-dot">{unread > 99 ? '99+' : unread}</span>}
            </NavLink>
            <UserMenu user={user} onSignOut={signOut} />
          </div>
        </header>

        <main className="shell-content">{children}</main>
      </div>
    </div>
  );
}

/**
 * Global search. Submits to /search rather than showing an inline dropdown:
 * results span tickets, incidents, customers and articles, which needs a page.
 */
function GlobalSearch() {
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // "/" focuses search, the convention in every operations tool.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <form
      className="shell-search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = value.trim();
        if (q.length >= 2) navigate(`/search?q=${encodeURIComponent(q)}`);
      }}
    >
      <SearchIcon size={14} className="shell-search-icon" />
      <input
        ref={inputRef}
        className="shell-search-input"
        placeholder="Search tickets, incidents, customers…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Search"
      />
      {!value && <kbd className="shell-search-kbd">/</kbd>}
    </form>
  );
}

function UserMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = (user?.name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div className="shell-user" ref={ref}>
      <button
        className="shell-avatar"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={user?.name}
      >
        {initials}
      </button>
      {open && (
        <div className="shell-menu" role="menu">
          <div className="shell-menu-head">
            <div className="strong truncate">{user?.name}</div>
            <div className="xs muted truncate">{user?.email}</div>
            <div className="xs subtle" style={{ marginTop: 3 }}>
              {user?.organization_name || user?.title || 'CliniqVoice Support'}
            </div>
          </div>
          <NavLink to="/profile" className="shell-menu-item" role="menuitem">
            <Settings size={14} /> Profile and notifications
          </NavLink>
          <button className="shell-menu-item" onClick={onSignOut} role="menuitem">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Navigation, assembled from permissions.
 *
 * Mirrors the structure in the product brief: Support for customers, Support
 * Operations for agents, Administration for admins.
 */
function buildNavigation({ isInternal, can }) {
  const sections = [];

  if (!isInternal) {
    sections.push({
      title: 'Support',
      items: [
        { to: '/portal', label: 'Overview', icon: LayoutDashboard, end: true },
        { to: '/portal/requests', label: 'My Requests', icon: Inbox },
        { to: '/portal/incidents', label: 'Incidents', icon: Siren },
        { to: '/kb', label: 'Knowledge Base', icon: BookOpen },
        { to: '/portal/plan', label: 'Support Plan', icon: CreditCard },
        { to: '/notifications', label: 'Notifications', icon: Bell },
      ],
    });

    if (can('customer.manage')) {
      sections.push({
        title: 'Organization',
        items: [
          { to: '/admin/users', label: 'Team', icon: Users },
          { to: '/ops/audit', label: 'Activity Log', icon: FileText },
        ],
      });
    }
    return sections;
  }

  const ops = [
    { to: '/ops/queue', label: 'Queue', icon: ListChecks },
    { to: '/ops/my-tickets', label: 'My Tickets', icon: ClipboardList },
  ];
  if (can('incident.view.internal')) ops.push({ to: '/ops/incidents', label: 'Incidents', icon: Siren });
  if (can('customer.context.view')) ops.push({ to: '/ops/customers', label: 'Customers', icon: Building2 });
  ops.push({ to: '/kb', label: 'Knowledge Base', icon: BookOpen });
  if (can('sla.monitor')) ops.push({ to: '/ops/sla', label: 'SLA Monitor', icon: Timer });
  if (can('analytics.view')) ops.push({ to: '/ops/analytics', label: 'Analytics', icon: Gauge });
  if (can('audit.view')) ops.push({ to: '/ops/audit', label: 'Audit Log', icon: FileText });
  if (can('feedback.view')) ops.push({ to: '/ops/feedback', label: 'Product Feedback', icon: MessageSquare });

  sections.push({ title: 'Support Operations', items: ops });

  // Reading a clinic's data is its own section rather than another queue item:
  // it is the one area where what staff may see is decided by the customer.
  if (can('platform.access')) {
    sections.push({
      title: 'Clinic Access',
      items: [
        { to: '/platform/access-requests', label: 'Access Requests', icon: KeyRound },
        { to: '/platform/logs', label: 'Clinic Logs', icon: ScrollText },
      ],
    });
  }

  const admin = [];
  if (can('plan.manage')) admin.push({ to: '/admin/plans', label: 'Support Plans', icon: CreditCard });
  if (can('sla.manage')) admin.push({ to: '/admin/sla', label: 'SLA Policies', icon: Timer });
  if (can('priority_rules.manage')) admin.push({ to: '/admin/priority', label: 'Priority Rules', icon: SlidersHorizontal });
  if (can('category.manage')) admin.push({ to: '/admin/categories', label: 'Categories', icon: Tags });
  if (can('user.manage') || can('team.manage')) {
    admin.push({ to: '/admin/users', label: 'Users & Teams', icon: Users });
  }
  if (can('notification_rules.manage')) admin.push({ to: '/admin/notifications', label: 'Notification Rules', icon: Bell });
  if (can('settings.manage')) admin.push({ to: '/admin/settings', label: 'System Settings', icon: Settings });

  if (admin.length) sections.push({ title: 'Administration', items: admin });

  return sections;
}

export { AlertTriangle };
